import { z } from "zod";
import { MAX_SPATIAL_SUBSPACES, serverConnectionStatusSchema, serverGenerationSchema, } from "./server-connection.js";
import { allocatedCapacitySchema, allocatedCcuSchema } from "./account-capacity.js";
import { PRODUCT_TYPE_POLICIES, productTypeSchema, tickRateHzSchema, } from "./product.js";
import { isoDateTimeSchema } from "./profile.js";
function validateProductTickRate(value, ctx) {
    if (value.productType === undefined || value.tickRateHz === undefined)
        return;
    const policy = PRODUCT_TYPE_POLICIES[value.productType];
    if (value.tickRateHz < policy.minTickRateHz || value.tickRateHz > policy.maxTickRateHz) {
        ctx.addIssue({
            code: "custom",
            message: `${value.productType} tickRateHz must be between ${policy.minTickRateHz} and ${policy.maxTickRateHz}`,
            path: ["tickRateHz"],
        });
    }
}
export const serverStatusSchema = z.enum([
    "pending",
    "provisioning",
    "running",
    "stopping",
    "stopped",
    "degraded",
    "failed",
]);
export const serverRegionSchema = z.enum([
    "us-central1",
    "us-east1",
    "us-east4",
    "us-west1",
    "europe-west1",
    "europe-west4",
    "europe-west9",
    "asia-northeast1",
    "asia-southeast1",
]);
/**
 * Desired configuration for a Woven server. Subspaces reference the
 * "spatial subspaces" concept from the product description: independent
 * spaces within a single server.
 */
export const serverConfigurationSchema = z.object({
    capacity: z.object({
        maxSubscribers: z.number().int().min(1).max(1_000_000),
        maxSubspaces: z.literal(MAX_SPATIAL_SUBSPACES),
    }),
    persistence: z.object({
        enabled: z.boolean(),
    }),
    inference: z.object({
        enabled: z.boolean(),
    }),
});
const disabledManagedFeatureSchema = z.object({ enabled: z.literal(false) });
export const serverConfigurationInputSchema = serverConfigurationSchema
    .extend({
    persistence: disabledManagedFeatureSchema,
    inference: disabledManagedFeatureSchema,
})
    .partial();
/** A compact, list-friendly projection of one server. */
export const serverSummarySchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1).max(64),
    region: serverRegionSchema,
    resourceId: z.literal("global").optional(),
    status: serverStatusSchema,
    productType: productTypeSchema.optional(),
    tickRateHz: tickRateHzSchema.optional(),
    allocatedCcu: allocatedCcuSchema.optional(),
    allocatedCapacity: allocatedCapacitySchema.optional(),
    reservedCapacity: allocatedCapacitySchema.optional(),
    capacityChangePending: z.boolean().optional(),
    pendingCapacity: z
        .object({
        productType: productTypeSchema,
        tickRateHz: tickRateHzSchema,
        allocatedCcu: allocatedCcuSchema,
        allocatedCapacity: allocatedCapacitySchema,
    })
        .superRefine((value, ctx) => {
        validateProductTickRate(value, ctx);
        if (value.allocatedCapacity !== value.tickRateHz * value.allocatedCcu)
            ctx.addIssue({
                code: "custom",
                message: "allocatedCapacity must equal tickRateHz * allocatedCcu",
                path: ["allocatedCapacity"],
            });
    })
        .optional(),
    createdAt: isoDateTimeSchema,
    connection: serverConnectionStatusSchema.optional(),
    generation: serverGenerationSchema.optional(),
    lifecycle: z
        .object({
        canProvision: z.boolean(),
        deleteIntent: z.boolean(),
        recoveryPending: z.boolean().optional(),
    })
        .optional(),
})
    .superRefine((value, ctx) => {
    validateProductTickRate(value, ctx);
    if (value.reservedCapacity !== undefined &&
        value.reservedCapacity <
            Math.max(value.allocatedCapacity ?? 0, value.pendingCapacity?.allocatedCapacity ?? 0))
        ctx.addIssue({
            code: "custom",
            message: "reservedCapacity must cover current and pending capacity",
            path: ["reservedCapacity"],
        });
    if (value.pendingCapacity !== undefined && value.capacityChangePending !== true)
        ctx.addIssue({
            code: "custom",
            message: "pendingCapacity requires capacityChangePending",
            path: ["pendingCapacity"],
        });
    if (value.allocatedCapacity !== undefined &&
        value.allocatedCcu !== undefined &&
        value.tickRateHz !== undefined &&
        value.allocatedCapacity !== value.tickRateHz * value.allocatedCcu) {
        ctx.addIssue({
            code: "custom",
            message: "allocatedCapacity must equal tickRateHz * allocatedCcu",
            path: ["allocatedCapacity"],
        });
    }
});
export const provisionServerResponseSchema = z.object({ server: serverSummarySchema });
export const recoverServerResponseSchema = provisionServerResponseSchema;
export const serverIdSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,64}$/)
    .refine((id) => !/^__.*__$/.test(id), { message: "Firestore-reserved ID" });
export const serverNameSchema = z.string().trim().min(1).max(64);
export const createServerRequestSchema = z
    .object({
    id: serverIdSchema,
    name: serverNameSchema,
    productType: productTypeSchema,
    tickRateHz: tickRateHzSchema,
    allocatedCcu: allocatedCcuSchema,
    resourceId: z.literal("global").default("global"),
    region: serverRegionSchema.default("us-central1").refine((region) => region === "us-central1", {
        message: "global registrations require us-central1",
    }),
    configuration: serverConfigurationInputSchema.optional(),
})
    .superRefine(validateProductTickRate);
export const updateServerCapacityRequestSchema = z
    .object({
    generation: serverGenerationSchema,
    productType: productTypeSchema,
    tickRateHz: tickRateHzSchema,
    allocatedCcu: allocatedCcuSchema,
})
    .strict()
    .superRefine(validateProductTickRate);
export const updateServerCapacityResponseSchema = z.object({ server: serverSummarySchema });
export const createServerResponseSchema = z.object({
    server: serverSummarySchema,
    configuration: serverConfigurationSchema,
});
export const listServersResponseSchema = z.object({
    items: z.array(serverSummarySchema),
    total: z.number().int().min(0),
});
//# sourceMappingURL=server.js.map